import { request } from './index';

export type AIClipStatus =
  | 'submitting' | 'queued' | 'running' | 'transferring' | 'succeeded' | 'failed' | 'cancelled';

export interface AIClipTask {
  id: string;
  episodeId: string;
  status: AIClipStatus;
  stage?: string;
  errorCode?: string;
  errorMessage?: string;
  videoDuration?: number;
  creditsDeducted?: number;
  createdAt: string;
  completedAt?: string;
  cancelRequestedAt?: string;
}

export interface AIClipStatusResponse {
  task: AIClipTask | null;
  hasSucceeded: boolean;
  videoPlayUrl?: string;
  premiereDownloadUrl?: string;
}

export interface AIClipTaskSummary {
  id: string;
  attemptNo: number;
  status: AIClipStatus;
  stage?: string;
  errorCode?: string;
  errorMessage?: string;
  videoDuration: number;
  creditsDeducted: number;
  createdBy: string;
  createdByUsername: string;
  createdAt: string;
  completedAt?: string;
  cancelRequestedAt?: string;
}

export interface AIClipHistoryPage {
  activeTask: AIClipTaskSummary | null;
  total?: number;
  items: AIClipTaskSummary[];
  nextCursor: string | null;
}

export interface AIClipTaskDetail extends AIClipTaskSummary {
  videoPlayUrl?: string;
  premiereDownloadUrl?: string;
}

export interface AIClipHistoryQuery {
  limit?: number;
  cursor?: string;
}

const path = (episodeId: string, suffix = ''): string =>
  `/episodes/${encodeURIComponent(episodeId)}/ai-clip${suffix}`;

const taskPath = (episodeId: string, taskId: string, suffix = ''): string =>
  path(episodeId, `/tasks/${encodeURIComponent(taskId)}${suffix}`);

export const getAIClipStatus = (episodeId: string): Promise<AIClipStatusResponse> =>
  request(path(episodeId));

export const triggerAIClip = (episodeId: string): Promise<AIClipTask> =>
  request(path(episodeId), { method: 'POST' });

export const getAIClipRecord = (episodeId: string): Promise<unknown> =>
  request(path(episodeId, '/record'));

export const getAIClipWarnings = (episodeId: string): Promise<unknown> =>
  request(path(episodeId, '/warnings'));

export const listAIClipTasks = (
  episodeId: string,
  query: AIClipHistoryQuery = {},
): Promise<AIClipHistoryPage> => {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.cursor !== undefined) params.set('cursor', query.cursor);
  const suffix = params.toString();
  return request(path(episodeId, `/tasks${suffix ? `?${suffix}` : ''}`));
};

export const getAIClipTask = (episodeId: string, taskId: string): Promise<AIClipTaskDetail> =>
  request(taskPath(episodeId, taskId));

export const getAIClipTaskRecord = (episodeId: string, taskId: string): Promise<unknown> =>
  request(taskPath(episodeId, taskId, '/record'));

export const getAIClipTaskWarnings = (episodeId: string, taskId: string): Promise<unknown> =>
  request(taskPath(episodeId, taskId, '/warnings'));

export const cancelAIClipTask = (episodeId: string, taskId: string): Promise<AIClipTaskSummary> =>
  request(taskPath(episodeId, taskId, '/cancel'), { method: 'POST' });
